import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import {
  Upload,
  Box,
  Gem,
  Trash2,
  UserCog,
  ArrowRight,
  Check,
  Sparkles,
  CreditCard,
  FolderOpen,
} from "lucide-react";
import { Card, Button, Pill, Skeleton, ProgressBar, useToast } from "@/shared/ui";
import { stagger, item } from "@/shared/motion/transitions";
import { useAuth } from "@/features/auth/context/AuthContext";
import { useUploads } from "@/features/vault/useUploads";
import { useApp } from "../vault/ctx";
import {
  listModels,
  getBilling,
  listPlans,
  createPortal,
  type VaultModel,
  type VaultPlan,
  type BillingState,
} from "@/features/vault/vaultApi";

const ACCEPT = ".glb,.gltf,.3dm,.obj,.fbx,.stl";

export default function HomePage() {
  const navigate = useNavigate();
  const { user, activeWorkspace } = useAuth();
  const { toast } = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const [recent, setRecent] = useState<VaultModel[] | null>(null);
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [plans, setPlans] = useState<VaultPlan[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const loadRecent = useCallback(() => {
    listModels(null, { recent: true }).then((m) => setRecent(m.slice(0, 8))).catch(() => setRecent([]));
  }, []);

  useEffect(() => {
    loadRecent();
    getBilling().then(setBilling).catch(() => setBilling(null));
    listPlans().then(setPlans).catch(() => setPlans([]));
  }, [loadRecent]);

  // Home uploads land in the drive root, so refresh both this page's "recent"
  // strip and any Drive listing the user navigates to next (see VaultCtx.bumpDrive).
  const { bumpDrive } = useApp();
  const onUploaded = useCallback(() => { loadRecent(); bumpDrive(); }, [loadRecent, bumpDrive]);
  const { uploads, uploadFiles } = useUploads(onUploaded);

  const firstName = (user?.name ?? "there").split(" ")[0];
  const planName = billing?.plan?.name ?? activeWorkspace?.planName ?? "Free";
  const upgrade = pickUpgrade(plans, planName);

  const openPortal = async () => {
    try {
      const { portalUrl } = await createPortal();
      window.location.href = portalUrl;
    } catch {
      toast({ tone: "info", title: "Billing portal unavailable", description: "No active subscription to manage yet." });
      navigate("/billing");
    }
  };

  const quickActions = [
    { label: "Browse Drive", icon: FolderOpen, onClick: () => navigate("/vault") },
    { label: "Ring Builder", icon: Gem, onClick: () => navigate("/ring") },
    { label: "Deleted Files", icon: Trash2, onClick: () => navigate("/vault?tab=deleted") },
    { label: "Profile Settings", icon: UserCog, onClick: () => navigate("/settings") },
  ];

  return (
    <div
      className="min-h-full px-6 py-7"
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        void uploadFiles(e.dataTransfer.files, null);
      }}
    >
      <motion.div variants={stagger} initial="hidden" animate="show" className="mx-auto max-w-5xl">
        {/* Greeting */}
        <motion.div variants={item} className="mb-6">
          <h1 className="font-headline text-[26px] text-ink-900">Welcome back, {firstName}</h1>
          <p className="mt-1 text-[13.5px] text-ink-500">
            {activeWorkspace?.workspaceName ? `${activeWorkspace.workspaceName} · ` : ""}Upload a model or jump back into your studio.
          </p>
        </motion.div>

        {/* Upload zone */}
        <motion.div variants={item}>
          <button
            onClick={() => fileInput.current?.click()}
            className={`flex w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center backdrop-blur transition-colors ${
              dragOver ? "border-[var(--accent)] bg-[var(--accent-soft)]" : "border-white/60 bg-white/35 hover:border-[var(--accent-border)] hover:bg-white/55"
            }`}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-gold-100 to-gold-50 text-gold-600">
              <Upload size={22} />
            </span>
            <p className="mt-3 text-[14px] font-semibold text-ink-900">Drag & drop files here</p>
            <p className="mt-1 text-[12px] text-ink-400">GLB, GLTF, 3DM, FBX, OBJ, STL</p>
            <span className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-ink-900 px-3.5 py-1.5 text-[12.5px] font-semibold text-white">
              Browse files
            </span>
          </button>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => void uploadFiles(e.target.files, null)}
          />
          {uploads.length > 0 && (
            <div className="mt-3 space-y-2">
              {uploads.map((u) => (
                <Card key={u.id} padding="sm" className="flex items-center gap-3 text-[13px]">
                  <span className="font-medium text-ink-900">{u.name}</span>
                  <span className="ml-auto w-40">
                    <ProgressBar value={u.progress} tone="gold" size="sm" animate />
                  </span>
                  <span className="w-20 text-right text-[11px] text-ink-400">{u.stage === "converting" ? "Optimizing…" : "Uploading…"}</span>
                </Card>
              ))}
            </div>
          )}
        </motion.div>

        {/* Quick actions */}
        <motion.div variants={item} className="mt-7">
          <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-400">Quick actions</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {quickActions.map((a) => (
              <button
                key={a.label}
                onClick={a.onClick}
                className="surface-card flex flex-col items-start gap-3 px-4 py-4 text-left transition-shadow hover:shadow-soft"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-ink-50 text-ink-700">
                  <a.icon size={17} />
                </span>
                <span className="text-[12.5px] font-semibold text-ink-900">{a.label}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Plan + upgrade */}
        <motion.div variants={item} className="mt-7 grid gap-3 lg:grid-cols-2">
          <Card padding="lg" className="flex flex-col">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-400">Your plan</p>
                <p className="mt-1.5 font-headline text-[20px] text-ink-900">{planName}</p>
              </div>
              <Pill tone="emerald">Active</Pill>
            </div>
            {billing?.subscription?.currentPeriodEnd && (
              <p className="mt-2 text-[12px] text-ink-500">
                Renews {new Date(billing.subscription.currentPeriodEnd).toLocaleDateString()}
              </p>
            )}
            {billing?.plan?.limits && (
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <Stat label="Models" value={billing.plan.limits.maxModels} />
                <Stat label="Domains" value={billing.plan.limits.maxDomains} />
                <Stat label="Embed views" value={billing.plan.limits.embedViews} />
              </div>
            )}
            <div className="mt-auto flex gap-2 pt-5">
              <Button variant="secondary" size="sm" onClick={openPortal} leadingIcon={<CreditCard size={14} />}>
                Manage
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate("/billing")}>
                Billing & invoices
              </Button>
            </div>
          </Card>

          {upgrade ? (
            <Card padding="lg" variant="inset" className="relative flex flex-col overflow-hidden">
              <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-gold-100 px-2 py-0.5 text-[10px] font-semibold text-gold-700">
                <Sparkles size={11} /> Upgrade
              </span>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-400">Special for you</p>
              <p className="mt-1.5 font-headline text-[20px] text-ink-900">{upgrade.name}</p>
              {upgrade.description && <p className="mt-1 text-[12.5px] text-ink-500">{upgrade.description}</p>}
              <ul className="mt-3 space-y-1.5">
                {upgrade.features.slice(0, 3).map((f) => (
                  <li key={f} className="flex items-center gap-2 text-[12.5px] text-ink-700">
                    <Check size={14} className="text-emerald" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-5">
                <Button fullWidth variant="gold" onClick={() => navigate("/billing/plans")} trailingIcon={<ArrowRight size={15} />}>
                  Upgrade plan
                </Button>
              </div>
            </Card>
          ) : (
            <Card padding="lg" variant="inset" className="flex flex-col items-start justify-center">
              <p className="font-headline text-[18px] text-ink-900">You're on the top plan 🎉</p>
              <p className="mt-1 text-[12.5px] text-ink-500">Manage your subscription anytime from Billing.</p>
            </Card>
          )}
        </motion.div>

        {/* Recent files */}
        <motion.div variants={item} className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-400">Recent files</h2>
            <button onClick={() => navigate("/vault")} className="text-[12px] font-medium text-gold-600 hover:text-gold-700">
              View all
            </button>
          </div>
          {recent === null ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="surface-card overflow-hidden">
                  <Skeleton shape="rect" className="aspect-[4/3] w-full" />
                  <div className="p-3"><Skeleton shape="text" className="h-3 w-3/4" /></div>
                </div>
              ))}
            </div>
          ) : recent.length === 0 ? (
            <Card padding="lg" variant="inset" className="text-center text-[13px] text-ink-400">
              No files yet — upload your first model above.
            </Card>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {recent.map((m) => (
                <motion.button
                  key={m._id}
                  whileHover={{ y: -2 }}
                  onClick={() => m.status === "ready" && navigate(`/view/${m._id}`)}
                  className="surface-card overflow-hidden text-left transition-shadow hover:shadow-soft"
                >
                  <div className="flex aspect-[4/3] items-center justify-center bg-gradient-to-br from-ink-50 to-canvas">
                    {m.posterUrl ? (
                      <img src={m.posterUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <Box size={24} className="text-ink-300" />
                    )}
                  </div>
                  <p className="truncate px-3 py-2.5 text-[12.5px] font-semibold text-ink-900">{m.name}</p>
                </motion.button>
              ))}
            </div>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-ink-50 px-2 py-2.5">
      <p className="text-[15px] font-semibold text-ink-900">{value < 0 ? "∞" : value.toLocaleString()}</p>
      <p className="text-[10.5px] text-ink-400">{label}</p>
    </div>
  );
}

/** Choose a sensible upsell: the cheapest plan priced above the current one. */
function pickUpgrade(plans: VaultPlan[], currentName: string): VaultPlan | null {
  if (!plans.length) return null;
  const sorted = [...plans].sort((a, b) => a.monthlyPrice - b.monthlyPrice);
  const current = sorted.find((p) => p.name === currentName);
  const currentPrice = current?.monthlyPrice ?? 0;
  return sorted.find((p) => p.monthlyPrice > currentPrice) ?? null;
}
